import { yieldToPaint } from './yieldPaint';

/** How many images to hand the decoder before the loader is allowed to paint. */
export const DECODE_BATCH = 4;

/** Run `jobs` a few at a time, yielding between batches so a tween can paint. */
export async function runBatched(
  jobs: Array<() => Promise<unknown>>,
  batchSize: number,
  yieldTurn: () => Promise<void>,
): Promise<void> {
  const size = Math.max(1, Math.floor(batchSize) || 1);
  for (let i = 0; i < jobs.length; i += size) {
    if (i > 0) await yieldTurn();
    const slice = jobs.slice(i, i + size);
    await Promise.all(slice.map((job) => Promise.resolve().then(job)));
  }
}

/** Ask the browser to finish decoding every HTML image the texture manager is holding. */
export async function decodeReadyTextures(textures: {
  getTextureKeys(): string[];
  get(key: string): { getSourceImage(): unknown };
}): Promise<void> {
  const jobs: Array<() => Promise<unknown>> = [];
  for (const key of textures.getTextureKeys()) {
    if (!key || key === '__MISSING' || key.startsWith('__')) continue;
    let source: unknown;
    try {
      source = textures.get(key).getSourceImage();
    } catch {
      continue;
    }
    if (!source || typeof source !== 'object' || !('decode' in source)) continue;
    const decode = (source as { decode?: () => Promise<void> }).decode;
    if (typeof decode !== 'function') continue;
    const image = source;
    jobs.push(() => decode.call(image).catch(() => undefined));
  }
  await runBatched(jobs, DECODE_BATCH, yieldToPaint);
}
